import { useEffect, useMemo, useState } from 'preact/hooks'
import { googleFontsHref, type FontRequest } from './brandGuidelinesModel'

export type FontLoad = 'loading' | 'loaded' | 'failed'

const MARK = 'data-cx-brand-font'
const STATE = 'data-state'

interface WantedFace {
  family: string
  href: string
  /** The same face with no weights asked for, tried when the weighted request fails; null when it asked for none. */
  withoutWeights: string | null
}

/**
 * Loads each face the brand's specimens are set in from Google Fonts, and says which ones failed (ORB-904).
 *
 * ⚑ One stylesheet per face, never one for all of them. css2 answers 400 to the whole request when any family in it is
 * unknown, so a single commercial face would take every specimen down to a system font with it.
 *
 * ⚑ css2 also answers 400 when a single-weight face (Bebas Neue, Anton) is asked for a weight it lacks, so a failed
 * weighted request is tried once more with no weights, and the browser synthesizes the rest. The weights are still
 * asked for first, because a multi-weight face loaded bare would arrive in 400 alone.
 *
 * ⚑ The link stays in the document after the view goes and records how it went on itself, so opening Brand Guidelines
 * again neither reloads a face nor forgets that one failed, even when it settled while nothing was listening.
 */
export function useBrandFonts(requests: readonly FontRequest[]): Record<string, FontLoad> {
  // The requests' whole content, so a new array holding the same faces loads nothing again.
  const key = JSON.stringify(requests)
  const wanted = useMemo<WantedFace[]>(
    () => (JSON.parse(key) as FontRequest[]).map(request => ({
      family: request.family,
      href: googleFontsHref(request),
      withoutWeights: request.weights.length > 0 ? googleFontsHref({ family: request.family, weights: [] }) : null,
    })),
    [key],
  )
  const [states, setStates] = useState<Record<string, FontLoad>>({})

  useEffect(() => {
    const cleanups: (() => void)[] = []
    // Runs after the link's own listeners, which `addLink` attached first, so the state is already recorded.
    const whenSettled = (link: HTMLLinkElement, settle: () => void) => {
      link.addEventListener('load', settle)
      link.addEventListener('error', settle)
      cleanups.push(() => {
        link.removeEventListener('load', settle)
        link.removeEventListener('error', settle)
      })
    }

    const initial: Record<string, FontLoad> = {}
    for (const face of wanted) {
      const report = () => setStates(current => ({ ...current, [face.family]: stateOfFace(face) }))
      const tryWithoutWeights = () => {
        if (face.withoutWeights === null) return
        const bare = existingLink(face.withoutWeights) ?? addLink(face.withoutWeights)
        if (stateOf(bare) === 'loading') whenSettled(bare, report)
      }

      const weighted = existingLink(face.href) ?? addLink(face.href)
      if (stateOf(weighted) === 'failed') tryWithoutWeights()
      else if (stateOf(weighted) === 'loading') {
        whenSettled(weighted, () => {
          if (stateOf(weighted) === 'failed') tryWithoutWeights()
          report()
        })
      }
      initial[face.family] = stateOfFace(face)
    }
    setStates(initial)
    return () => cleanups.forEach(cleanup => cleanup())
  }, [wanted])

  return states
}

/** The weighted request's state, unless it failed and a request with no weights is still to settle or has. */
function stateOfFace(face: WantedFace): FontLoad {
  const weighted = existingLink(face.href)
  const first = weighted ? stateOf(weighted) : 'loading'
  if (first !== 'failed' || face.withoutWeights === null) return first
  const bare = existingLink(face.withoutWeights)
  return bare ? stateOf(bare) : 'loading'
}

function stateOf(link: HTMLLinkElement): FontLoad {
  const state = link.getAttribute(STATE)
  return state === 'loaded' || state === 'failed' ? state : 'loading'
}

function existingLink(href: string): HTMLLinkElement | null {
  return [...document.head.querySelectorAll<HTMLLinkElement>(`link[${MARK}]`)].find(link => link.getAttribute(MARK) === href) ?? null
}

function addLink(href: string): HTMLLinkElement {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.setAttribute(MARK, href)
  link.addEventListener('load', () => link.setAttribute(STATE, 'loaded'), { once: true })
  link.addEventListener('error', () => link.setAttribute(STATE, 'failed'), { once: true })
  document.head.appendChild(link)
  return link
}
